import { Box, CardMedia, IconButton, Typography } from '@mui/material';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import Carousel from 'react-material-ui-carousel';
import { useInView } from 'react-intersection-observer';
import { CarouselProps } from 'react-material-ui-carousel/dist/components/types';
import { userImgUrl } from 'utilities/userImageURL';
import CustomVideoPlayer from './customVideoPlayer';
import { useAppSelector } from 'store/hook';
import { RootState } from 'store';
import { IGuestModeOptions } from 'models/apiPaths';
import { isAudio } from 'utilities/helpers';
import {
  ZoomShrink,
  ZoomReset,
  ZoomIn,
  ZoomExpand,
  ZoomOut,
  MusicIcon
} from 'assets';
import {
  TransformWrapper,
  TransformComponent,
  useControls
} from "react-zoom-pan-pinch";

import { getMediaAltText } from 'utilities/mediaAlt';

export type IMediaItems = {
  fileName: string;
  directUrl: string;
  thumbnailUrl?: string;
  altText?: string;
  postContext?: any;
  [key: string]: any;
};

interface Props extends CarouselProps {
  mediaDtos: IMediaItems[];
  postId?: string;
  scrolling?: boolean;
  activeIndex?: number;
  setActiveIndex?: (value: number) => void;
  setFullScreen?: (value: boolean) => void;
  setMediaItems?: (mediaItems: IMediaItems[]) => void;
  setVideoMedia?: (value: boolean) => void;
  setGuestModeOptions?: (value: IGuestModeOptions) => void;
  setOpenSigninDialog?: (value: boolean) => void;
  imgSx?: React.CSSProperties;
  videoSx?: React.CSSProperties;
  fullScreenView?: boolean;
  enableZoom?: boolean;
  fromComponent?: string;
  fromMediaTab?: boolean;
  openFullScreen?: (isFullscreen: boolean) => void;
  altText?: string;
  postContext?: any;
}

const LazyMedia = React.memo(
  ({
    src,
    isVideo,
    isAudio,
    imgSx,
    videoSx,
    pauseScrollChange,
    pauseVideoChange,
    preload = false,
    enableZoom = false,
    isResetZoom = false,
    thumbnailUrl,
    fullScreenView,
    fromMediaTab,
    callBack,
    openFullScreen,
    altText,
  }: {
    src: string;
    isVideo: boolean;
    isAudio: boolean;
    imgSx?: React.CSSProperties;
    videoSx?: React.CSSProperties;
    pauseScrollChange: boolean;
    pauseVideoChange: boolean;
    preload?: boolean;
    enableZoom?: boolean;
    isResetZoom?: boolean;
    thumbnailUrl?: string;
    fullScreenView?: boolean;
    fromMediaTab?: boolean;
    callBack?: () => void;
    openFullScreen?: (isFullscreen: boolean) => void;
    fileName?: string;
    altText?: string;
  }) => {
    const { ref, inView } = useInView({
      triggerOnce: true,
      threshold: 0.1,
      rootMargin: '1000px'
    });
    const isVisible = inView || preload;

    React.useEffect(() => {
      if (!isVideo && isVisible) {
        const img = new Image();
        img.src = src;
      }
    }, [isVisible, isVideo, src]);
    const [expand, setExpand] = useState(false);
    const audioRef = useRef<HTMLAudioElement>(null);
    useEffect(() => {
      if (isAudio && audioRef.current && (!pauseVideoChange || !pauseScrollChange)) {
        audioRef.current.pause();
      }
    }, [pauseVideoChange, pauseScrollChange, isAudio]);
    const Controls = () => {
      const { zoomIn, zoomOut, resetTransform, setTransform } = useControls();

      useEffect(()=>{
        isResetZoom && resetTransform();
      },[isResetZoom, resetTransform]);
      
      return (
        <div
            style={{
              position: 'absolute',
              bottom: fromMediaTab ? '50px':'30px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1000,
              display: 'flex',
              padding: fromMediaTab ?'5px': '10px',
              width: '164px',
              height: '44px',
              borderRadius: '4px',
              backdropFilter: 'blur(4px)',
              background: 'rgba(0, 0, 0, 0.3)',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <IconButton
              title="Zoom in"
              onClick={() => zoomIn()}
              style={zoomButtonStyle}
              sx={{ ':hover': { backgroundColor: 'rgb(0,0,0,0)' } }}
            >
            <ZoomIn />
            </IconButton>
            <IconButton
              title="Zoom out"
              onClick={() => zoomOut()}
              style={zoomButtonStyle}
              sx={{ ':hover': { backgroundColor: 'rgb(0,0,0,0)' } }}
            >
              <ZoomOut />
            </IconButton>
            <IconButton title="Default"
              onClick={() => {
                setExpand(false);
                resetTransform();
              }}
              style={zoomButtonStyle}
              sx={{ ':hover': { backgroundColor: 'rgb(0,0,0,0)' } }}
            >
              <ZoomReset/>
            </IconButton>
            {fromMediaTab && (expand ? (
              <IconButton
                title="Collapse"
                onClick={() => {
                  setExpand(false);
                  openFullScreen?.(false);
                  // resetTransform();
                }}
                style={zoomButtonStyle}
                sx={{ ':hover': { backgroundColor: 'rgb(0,0,0,0)' } }}
              >
                <ZoomShrink />
              </IconButton>
            ) : (
              <IconButton
                title="Full Screen"
                onClick={() => {
                  setExpand(true);
                  openFullScreen?.(true);
                  // setTransform(-window.innerWidth, -window.innerHeight, 3);
                }}
                style={zoomButtonStyle}
                sx={{ ':hover': { backgroundColor: 'rgb(0,0,0,0)' } }}
              >
                <ZoomExpand />
              </IconButton>
            ))}
        </div>
      );
    };

    const cardMedia = () => {
      return (
        isAudio ? (
<Box sx={{ 
        position: 'relative', 
        width: '100%', 
        height: fullScreenView || fromMediaTab ? '100vh' : '100%', 
        bgcolor: '#2a2a2a', 
        borderRadius: fullScreenView || fromMediaTab ? 0 : 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Box
          sx={{
            bgcolor: 'rgba(0, 0, 0, 0.4)',
            borderRadius: '50%',
            width: fullScreenView || fromMediaTab ? 120 : 60,
            height: fullScreenView || fromMediaTab ? 120 : 60,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <MusicIcon style={{ 
            width: fullScreenView || fromMediaTab ? 80 : 64, 
            height: fullScreenView || fromMediaTab ? 80 : 64, 
            color: '#fff' 
          }} />
        </Box>
        <Box sx={{ 
          position: 'absolute', 
          bottom: fullScreenView || fromMediaTab ? 100 : 16,
          left: '50%', 
          transform: 'translateX(-50%)', 
          width: fullScreenView || fromMediaTab ? '60%' : '85%',
          maxWidth: '600px'
        }}>
          <audio 
            controls 
            src={isVisible ? src : ''}
            style={{ width: '100%' }}
            ref={audioRef}
            
          >
            Your browser does not support the audio element.
          </audio>
        </Box>
      </Box>
    ) : !isVideo ? (
        <>
          <CardMedia
            component='img'
            image={isVisible ? src : ''}
            alt={altText || 'User uploaded post image'}
            loading='lazy'
            sx={{
              width: '100%',
              height: imgSx?.height ?? '100%',
              position: 'absolute',
              left: 0,
              top: 0,
              zIndex: 1,
              opacity: 0.2,
              transform: 'scale(1.2)',
              filter: 'blur(0px)',
              transition: 'opacity 0.3s ease-in-out'
            }}
          />

          <CardMedia
            component='img'
            image={isVisible ? src : ''}
            alt={altText || 'User uploaded post image'}
            loading='lazy'
            sx={{
              width: 'auto',
              height: 'auto',
              maxWidth: '100%',
              objectFit: 'contain',
              position: 'relative',
              zIndex: 2,
              opacity: isVisible ? 1 : 0,
              transition: 'opacity 0.3s ease-in-out',
              ...imgSx
            }}
          />
        </>
         ) : (
          <CustomVideoPlayer
            url={isVisible ? src : ''}
            thumbnailUrl={!(fullScreenView || fromMediaTab) && thumbnailUrl ? thumbnailUrl : undefined}
            pauseVideoChange={pauseVideoChange}
            pauseScrollChange={pauseScrollChange}
            videoSx={videoSx}
            fullScreenView={fullScreenView || fromMediaTab}
            callBack={callBack}
          />
        )
      )
    }
    return (
      <Box
        ref={ref}
        sx={{
          position: 'relative',
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          visibility: preload ? 'hidden' : 'visible'
        }}
      >
        {/* Zoom Controls (Visible only if enableZoom is true) */
          enableZoom ? (
            <TransformWrapper
              disablePadding={true}
              disabled={!enableZoom}
              wheel={{
                disabled: !enableZoom,
                wheelDisabled: !enableZoom,
                touchPadDisabled: !enableZoom
              }}
              panning= {{
                disabled: !enableZoom,
                velocityDisabled: !enableZoom
              }}
            >
              {enableZoom && (<Controls />)}
              <TransformComponent wrapperStyle={{width: '100%', height: '100%'}} contentStyle={{width: '100%', height: '100%'}}>
                {cardMedia()}
              </TransformComponent>
            </TransformWrapper>
          ) : (
            cardMedia()
          )}
      </Box>
    );
  }
);

const CarousalItems = ({
  mediaDtos,
  postId,
  scrolling = false,
  activeIndex,
  setActiveIndex,
  setFullScreen,
  setMediaItems,
  setVideoMedia,
  setGuestModeOptions,
  setOpenSigninDialog,
  imgSx,
  videoSx,
  fullScreenView,
  enableZoom,
  fromComponent,
  fromMediaTab,
  openFullScreen,
  altText,
  postContext,
  ...carouselProps
}: Props) => {
  const [activePlay, setActivePlay] = useState<number>(
    (fullScreenView || fromMediaTab) ? activeIndex || 0 : 0
  );

  const [isResetZoom, setIsResetZoom] = useState<boolean>(false);

  const { isLoggedIn }: any = useAppSelector(
    (state: RootState) => state.userAuth
  );

  const isVideo = useCallback(
    (fileName: string) =>
      ['.mp4', '.mov','.webm'].some((ext) => fileName.toLowerCase().endsWith(ext)),
    []
  );
  const getImageUrl = useCallback(
    (item: IMediaItems) => item.directUrl || userImgUrl(item.id),
    []
  );

  const handleSelect = useCallback((now?: number) => {
    if (now !== undefined) {
      setActivePlay(now);
      setIsResetZoom(true);
    }
  }, []);

  useEffect(() => {
    if (fromMediaTab) {
      setActivePlay(activeIndex || 0);
    }
  }, [fromMediaTab, setActivePlay, activeIndex]);

  useEffect(() => {
  // Pause all audio elements
  const allAudios = document.querySelectorAll('audio');
  allAudios.forEach(audio => {
    audio.pause();
    audio.currentTime = 0; // optional reset to beginning
  });
}, [activePlay]);
  

  return (
    <Carousel
      onChange={handleSelect}
      index={activeIndex}
      navButtonsAlwaysVisible={mediaDtos.length > 1}
      indicators={mediaDtos.length > 1 && !fullScreenView}
      cycleNavigation={false}
      autoPlay={false}
      animation={mediaDtos.length > 2 ? 'slide' : undefined}
      navButtonsWrapperProps={{ style: { top: '45%', height: 'auto' } }}
      swipe={false}
      {...(fromMediaTab && {
        indicatorContainerProps: {
          style: {
            position: 'absolute',
            bottom: '30px',
            left: 0,
            right: 0,
            zIndex: 2,
            display: 'flex',
            justifyContent: 'center',
          },
        },
      })}
      {...carouselProps}
    >
      {mediaDtos.map((item, index) => {
        const mediaUrl = getImageUrl(item);
        const isMediaVideo = isVideo(item.fileName);
        const isNextItem = activePlay + 1 === index || activePlay - 1 === index;
        const isMediaAudio = isAudio(item.fileName);
        const callBack = () => {
          if (isLoggedIn) {
            if (!item.thumbnailUrl || (item.thumbnailUrl && item.thumbnailUsed)) {
              setFullScreen?.(true);
              setMediaItems?.(mediaDtos);
            } else if (!fullScreenView){
              item.thumbnailUsed = true;
            }
            setActiveIndex?.(index);
            setVideoMedia?.(isMediaVideo);
          } else {
            /* open signin modal */
            setGuestModeOptions?.({
              guestMode: true,
              navigateTo: 'post',
              menu: 'post',
              redirectTo: '/post/' + postId,
              action: 'post',
              actionItem: postId ?? null
            });
            setOpenSigninDialog?.(true);
          }
        }
            const itemAlt = getMediaAltText(item, item.postContext || postContext);
            return (
              <Box
                key={index}
                sx={{
                  width: '100%',
                  height: fullScreenView  || fromMediaTab? 'auto' :'400px',
                  cursor: 'pointer',
                  ...(mediaDtos.length === 2 && {
                    transform: `translateX(${activePlay === index ? '0%' : index === 0 ? '-100%' : '100%'})`,
                    transition: 'transform 0.5s ease-in-out'
                  })
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  callBack();
                }}
              >
                <LazyMedia
                  src={mediaUrl}
                  isVideo={isMediaVideo}
                  isAudio={isMediaAudio}
                  imgSx={imgSx}
                  videoSx={videoSx}
                  pauseVideoChange={activePlay === index}
                  pauseScrollChange={scrolling}
                  preload={isNextItem}
                  enableZoom={enableZoom}
                  isResetZoom={isResetZoom}
                  thumbnailUrl={item.thumbnailUrl}
                  fullScreenView={fullScreenView}
                  fromMediaTab= {fromMediaTab}
                  callBack={callBack}
                  openFullScreen={openFullScreen}
                  altText={itemAlt}
                />
              </Box>
            );
      })}
    </Carousel>
  );
};

// Common zoom button styling
const zoomButtonStyle = {
  color: '#fff',
  borderRadius: '50%',
  padding: '10px'
};

export default React.memo(CarousalItems);
